import React, { useEffect, useState, useRef, memo } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { checkUnsupportedGlyphs } from '../../../utils/fontHelpers';
import {
  decorationLineStyle,
  fontCacheKey,
  textLegendLayout,
  textLegendStyle,
} from '../../../utils/textLegend';
import { getContrastingColor } from '../../../utils/colorHelpers';

const RenderKeycapTexts = memo(
  // using React.memo to ensure that RenderKeycapTexts only re-renders when its props change (before that, it re-rendered every time a setHoveredKeycap was triggered in the KeycapOverlay.js file)
  ({
    keycapColor,
    selectedKeycapRowIndex,
    selectedKeycapIndex,
    selectedTextIndex,
    texts,
    innerDPath,
    innerDPath1mmPadding,
    rowIndex,
    keycapIndex,
    printableAreas,
    showGrid,
    show2x2Grid,
    show3x3Grid,
    showBoundingBox,
    showPrintableArea,
    showTopSurface,
    selectedEditor,
    dataComingFromLayoutEditor,
    dataComingFromLegendsEditor,
    fonts,
    setFonts,
    dataComingFromProcessOrderScreen,
    loadFont,
    orderItemIndex,
    runIndex,
  }) => {
    const {
      innerRect1X,
      innerRect1Y,
      innerRect1Width,
      innerRect1Height,
      innerRect2X,
      innerRect2Y,
      innerRect2Width,
      innerRect2Height,
    } = printableAreas; // the keycap type's printable rectangles (keycapPrintableAreas.js): the clip path and the printable-area outline below, and getLegendPosition takes the object

    const { t } = useTranslation();

    const pathRef = useRef(null); // a reference to the inner path of the keycap (innerDPath1mmPadding)
    const [bbox, setBBox] = useState(null); // bounding box of the inner path of the keycap (innerDPath1mmPadding)
    const [previousTexts, setPreviousTexts] = useState({}); // Track the previous state of texts (to determine if the text in a specific position on the keycap has changed)

    const clipPathId = dataComingFromProcessOrderScreen
      ? `text-clipPath-${orderItemIndex}-${runIndex}-${keycapIndex}`
      : `text-clipPath-${rowIndex}-${keycapIndex}`; // unique id
    const innerPathId = dataComingFromProcessOrderScreen
      ? `text-innerPath-${orderItemIndex}-${runIndex}-${keycapIndex}`
      : `text-innerPath-${rowIndex}-${keycapIndex}`; // unique id
    const printableAreaId = dataComingFromProcessOrderScreen
      ? `text-printable-area-${orderItemIndex}-${runIndex}-${keycapIndex}`
      : `text-printable-area-${rowIndex}-${keycapIndex}`; // unique id
    const topSurfaceId = dataComingFromProcessOrderScreen
      ? `text-top-surface-${orderItemIndex}-${runIndex}-${keycapIndex}`
      : `text-top-surface-${rowIndex}-${keycapIndex}`; // unique id

    useEffect(() => {
      if (texts && Object.keys(texts).length > 0) {
        // Loop over each text entry in the texts object

        Object.entries(texts).forEach(
          async ([position, { text, fontFamilyName, style }]) => {
            if (fontFamilyName && style && style.fontFamily) {
              const fontFamily = style.fontFamily;
              const cacheKey = fontCacheKey(fontFamily); // the key the font is cached and registered under (utils/textLegend.js)

              await loadFont(
                cacheKey,
                `/api/fonts/ttf/${fontFamilyName}?fontName=${fontFamily}`
              );
            }
          }
        );

        setPreviousTexts(texts); // Update previousTexts to reflect the current state of texts
      }
    }, [texts, loadFont]);

    useEffect(() => {
      if (pathRef.current) {
        const boundingBox = pathRef.current.getBBox();
        setBBox(boundingBox);
      }
    }, [innerDPath1mmPadding]); // calculating the bounding box of the innerDPath1mmPadding path (after it renders)

    useEffect(() => {
      if (
        texts &&
        Object.keys(texts).length > 0 &&
        dataComingFromLegendsEditor // check for unsupported glyphs, if the texts are being rendered in the legends editor
      ) {
        // Iterate through each text object in 'texts'
        Object.entries(texts).forEach(
          ([position, { fontFamilyName, text, style }]) => {
            const prevText = previousTexts?.[position]?.text; // Get the previous text at the current position
            const { fontFamily } = style;
            const cacheKey = fontCacheKey(fontFamily); // the unique cache key used to access the cached font that is being used for this text
            const currentFont = fonts[cacheKey]; // Retrieve the correct font from the fonts state using the cache key

            if (text && text !== prevText && currentFont) {
              // if text has changed and the font is loaded:
              const unsupported = checkUnsupportedGlyphs(currentFont, text); // Check for unsupported glyphs with the correct font

              if (unsupported.length > 0) {
                // If unsupported glyphs are found:
                const boldGlyphs = unsupported.map((glyph, index) => (
                  <strong key={index}>{glyph}</strong>
                )); // Format unsupported glyphs in bold for better readability

                toast.error(
                  <div>
                    {t(
                      'render-keycap-texts-component-remove-non-supported-glyphs'
                    )}
                    <br />
                    {boldGlyphs.reduce((prev, curr) => [prev, ', ', curr])}
                  </div>
                ); // Display warning message with unsupported glyphs
              }
            }
          }
        );

        setPreviousTexts(texts); // Update previousTexts to reflect the current state of texts
      }
    }, [texts, fonts]); // This useEffect only checks for unsupported glyphs in the LegendsEditScreen. This is to inform the admin during creation of predefined legends, if any of the predefined legends have glyphs that are not supported by the default font (set in the LegendsEditScreen) and notifies the admin.

    return (
      <g>
        {/* defining a unique clipPath for this keycap using innerDPath1mmPadding: */}
        <defs>
          <clipPath id={clipPathId}>
            {/* Printable Area is currently disabled:
              <path d={innerDPath1mmPadding} />
            */}

            {/* The "new" printable area (grid), which is used instead of the original: */}
            {/* (values for Rect2 are only present for the ISO Enter key) */}
            <rect
              x={innerRect1X}
              y={innerRect1Y}
              width={innerRect1Width}
              height={innerRect1Height}
              fill="none"
            />
            {innerRect2X !== null &&
            innerRect2Y !== null &&
            innerRect2Width !== null &&
            innerRect2Height !== null ? (
              <rect
                x={innerRect2X}
                y={innerRect2Y}
                width={innerRect2Width}
                height={innerRect2Height}
                fill="none"
              />
            ) : null}
          </clipPath>
        </defs>

        <path
          ref={pathRef} // the path to calculate the bbox for the clipPath of the printable area
          d={innerDPath1mmPadding}
          fill="none" // not visible to the user
          id={innerPathId}
        />

        {/* Printable Area is currently disabled:
          {!dataComingFromLayoutEditor &&
            !dataComingFromLegendsEditor &&
            !dataComingFromProcessOrderScreen && (
              // if the text is being rendered in the Customizer:
              <path
                className="dont-include-in-download" // don't include this path in the svg and png downloads
                d={innerDPath1mmPadding}
                fill="none"
                stroke={getContrastingColor(keycapColor)}
                strokeWidth={
                  selectedEditor === 'text' &&
                  showPrintableArea &&
                  selectedKeycapRowIndex === rowIndex &&
                  selectedKeycapIndex === keycapIndex &&
                  selectedTextIndex
                    ? '0.1'
                    : '0'
                } // conditionally rendering the path to make the printable area visible
                id={printableAreaId}
              />
            )}
         */}

        {dataComingFromProcessOrderScreen && (
          // if the texts are being rendered in the ProcessOrderScreen, conditionally display the printableArea and grid
          <g>
            {showPrintableArea && (
              <path
                d={innerDPath1mmPadding}
                fill="none"
                stroke={getContrastingColor(keycapColor)}
                strokeWidth={'0.1'}
                id={printableAreaId}
              />
            )}

            {showTopSurface && (
              <path
                d={innerDPath}
                fill="none"
                stroke={getContrastingColor(keycapColor)}
                strokeWidth={'0.1'}
                id={topSurfaceId}
              />
            )}

            {show2x2Grid && (
              <g>
                <rect
                  x={innerRect1X}
                  y={innerRect1Y}
                  width={innerRect1Width}
                  height={innerRect1Height}
                  fill="none"
                  stroke={getContrastingColor(keycapColor)}
                  strokeWidth="0.1"
                />
                <rect
                  x={innerRect2X}
                  y={innerRect2Y}
                  width={innerRect2Width}
                  height={innerRect2Height}
                  fill="none"
                  stroke={getContrastingColor(keycapColor)}
                  strokeWidth="0.1"
                />

                {/* Render lines for 3x3 grid */}
                {/* Vertical line */}
                <line
                  x1={innerRect1X + innerRect1Width / 2}
                  y1={innerRect1Y}
                  x2={innerRect1X + innerRect1Width / 2}
                  y2={innerRect1Y + innerRect1Height}
                  stroke={getContrastingColor(keycapColor)}
                  strokeWidth="0.1"
                />
                {/* Horizontal line */}
                <line
                  x1={innerRect1X}
                  y1={innerRect1Y + innerRect1Height / 2}
                  x2={innerRect1X + innerRect1Width}
                  y2={innerRect1Y + innerRect1Height / 2}
                  stroke={getContrastingColor(keycapColor)}
                  strokeWidth="0.1"
                />

                {/* Render lines for 3x3 grid */}
                {/* Vertical line */}
                <line
                  x1={innerRect2X + innerRect2Width / 2}
                  y1={innerRect2Y}
                  x2={innerRect2X + innerRect2Width / 2}
                  y2={innerRect2Y + innerRect2Height}
                  stroke={getContrastingColor(keycapColor)}
                  strokeWidth="0.1"
                />

                {/* Horizontal line */}
                <line
                  x1={innerRect2X}
                  y1={innerRect2Y + innerRect2Height / 2}
                  x2={innerRect2X + innerRect2Width}
                  y2={innerRect2Y + innerRect2Height / 2}
                  stroke={getContrastingColor(keycapColor)}
                  strokeWidth="0.1"
                />
              </g>
            )}

            {show3x3Grid && (
              <g>
                <rect
                  x={innerRect1X}
                  y={innerRect1Y}
                  width={innerRect1Width}
                  height={innerRect1Height}
                  fill="none"
                  stroke={getContrastingColor(keycapColor)}
                  strokeWidth="0.1"
                />
                <rect
                  x={innerRect2X}
                  y={innerRect2Y}
                  width={innerRect2Width}
                  height={innerRect2Height}
                  fill="none"
                  stroke={getContrastingColor(keycapColor)}
                  strokeWidth="0.1"
                />

                {/* Render lines for 3x3 grid */}
                {/* Vertical lines */}
                <line
                  x1={innerRect1X + innerRect1Width / 3}
                  y1={innerRect1Y}
                  x2={innerRect1X + innerRect1Width / 3}
                  y2={innerRect1Y + innerRect1Height}
                  stroke={getContrastingColor(keycapColor)}
                  strokeWidth="0.1"
                />
                <line
                  x1={innerRect1X + (innerRect1Width / 3) * 2}
                  y1={innerRect1Y}
                  x2={innerRect1X + (innerRect1Width / 3) * 2}
                  y2={innerRect1Y + innerRect1Height}
                  stroke={getContrastingColor(keycapColor)}
                  strokeWidth="0.1"
                />
                {/* Horizontal lines */}
                <line
                  x1={innerRect1X}
                  y1={innerRect1Y + innerRect1Height / 3}
                  x2={innerRect1X + innerRect1Width}
                  y2={innerRect1Y + innerRect1Height / 3}
                  stroke={getContrastingColor(keycapColor)}
                  strokeWidth="0.1"
                />
                <line
                  x1={innerRect1X}
                  y1={innerRect1Y + (innerRect1Height / 3) * 2}
                  x2={innerRect1X + innerRect1Width}
                  y2={innerRect1Y + (innerRect1Height / 3) * 2}
                  stroke={getContrastingColor(keycapColor)}
                  strokeWidth="0.1"
                />

                {/* Render lines for 3x3 grid */}
                {/* Vertical lines */}
                <line
                  x1={innerRect2X + innerRect2Width / 3}
                  y1={innerRect2Y}
                  x2={innerRect2X + innerRect2Width / 3}
                  y2={innerRect2Y + innerRect2Height}
                  stroke={getContrastingColor(keycapColor)}
                  strokeWidth="0.1"
                />
                <line
                  x1={innerRect2X + (innerRect2Width / 3) * 2}
                  y1={innerRect2Y}
                  x2={innerRect2X + (innerRect2Width / 3) * 2}
                  y2={innerRect2Y + innerRect2Height}
                  stroke={getContrastingColor(keycapColor)}
                  strokeWidth="0.1"
                />
                {/* Horizontal lines */}
                <line
                  x1={innerRect2X}
                  y1={innerRect2Y + innerRect2Height / 3}
                  x2={innerRect2X + innerRect2Width}
                  y2={innerRect2Y + innerRect2Height / 3}
                  stroke={getContrastingColor(keycapColor)}
                  strokeWidth="0.1"
                />
                <line
                  x1={innerRect2X}
                  y1={innerRect2Y + (innerRect2Height / 3) * 2}
                  x2={innerRect2X + innerRect2Width}
                  y2={innerRect2Y + (innerRect2Height / 3) * 2}
                  stroke={getContrastingColor(keycapColor)}
                  strokeWidth="0.1"
                />
              </g>
            )}
          </g>
        )}

        {bbox &&
          texts &&
          Object.keys(texts).length > 0 && // Ensure texts is defined and not empty
          Object.entries(texts)
            .filter(([_, { text }]) => text && text.trim() !== '') // filtering out empty strings (this ensures that only text elements are being rendered onto the keycaps, if the string is not empty)
            .map(([index, { position }]) => {
              const { gridStructure, isoSection } = position; // Extract gridStructure from position

              if (
                selectedEditor === 'text' &&
                showGrid &&
                selectedKeycapRowIndex === rowIndex &&
                selectedKeycapIndex === keycapIndex &&
                Number(selectedTextIndex) === Number(index)
              ) {
                return (
                  <g
                    className="dont-include-in-download" // don't include the grid in the svg and png downloads
                    key={index}
                  >
                    {/* Render grid outer rect */}
                    <g>
                      {isoSection === 'top' || isoSection === 'none' ? (
                        <rect
                          x={innerRect1X}
                          y={innerRect1Y}
                          width={innerRect1Width}
                          height={innerRect1Height}
                          fill="none"
                          stroke={getContrastingColor(keycapColor)}
                          strokeWidth="0.1"
                        />
                      ) : isoSection === 'main' &&
                        innerRect2X !== null &&
                        innerRect2Y !== null &&
                        innerRect2Width !== null &&
                        innerRect2Height !== null ? (
                        <rect
                          x={innerRect2X}
                          y={innerRect2Y}
                          width={innerRect2Width}
                          height={innerRect2Height}
                          fill="none"
                          stroke={getContrastingColor(keycapColor)}
                          strokeWidth="0.1"
                        />
                      ) : null}
                    </g>

                    {/* Render grid lines based on gridStructure */}
                    {(isoSection === 'top' || isoSection === 'none') && (
                      <g>
                        {gridStructure === '3x3' && (
                          <g>
                            {/* Render lines for 3x3 grid */}
                            {/* Vertical lines */}
                            <line
                              x1={innerRect1X + innerRect1Width / 3}
                              y1={innerRect1Y}
                              x2={innerRect1X + innerRect1Width / 3}
                              y2={innerRect1Y + innerRect1Height}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                            <line
                              x1={innerRect1X + (innerRect1Width / 3) * 2}
                              y1={innerRect1Y}
                              x2={innerRect1X + (innerRect1Width / 3) * 2}
                              y2={innerRect1Y + innerRect1Height}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                            {/* Horizontal lines */}
                            <line
                              x1={innerRect1X}
                              y1={innerRect1Y + innerRect1Height / 3}
                              x2={innerRect1X + innerRect1Width}
                              y2={innerRect1Y + innerRect1Height / 3}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                            <line
                              x1={innerRect1X}
                              y1={innerRect1Y + (innerRect1Height / 3) * 2}
                              x2={innerRect1X + innerRect1Width}
                              y2={innerRect1Y + (innerRect1Height / 3) * 2}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                          </g>
                        )}
                        {gridStructure === '3x2' && (
                          <g>
                            {/* Render lines for 3x2 grid */}
                            {/* Vertical line */}
                            <line
                              x1={innerRect1X + innerRect1Width / 2}
                              y1={innerRect1Y}
                              x2={innerRect1X + innerRect1Width / 2}
                              y2={innerRect1Y + innerRect1Height}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                            {/* Horizontal lines */}
                            <line
                              x1={innerRect1X}
                              y1={innerRect1Y + innerRect1Height / 3}
                              x2={innerRect1X + innerRect1Width}
                              y2={innerRect1Y + innerRect1Height / 3}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                            <line
                              x1={innerRect1X}
                              y1={innerRect1Y + (innerRect1Height / 3) * 2}
                              x2={innerRect1X + innerRect1Width}
                              y2={innerRect1Y + (innerRect1Height / 3) * 2}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                          </g>
                        )}
                        {gridStructure === '2x3' && (
                          <g>
                            {/* Render lines for 2x3 grid */}
                            {/* Vertical lines */}
                            <line
                              x1={innerRect1X + innerRect1Width / 3}
                              y1={innerRect1Y}
                              x2={innerRect1X + innerRect1Width / 3}
                              y2={innerRect1Y + innerRect1Height}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                            <line
                              x1={innerRect1X + (innerRect1Width / 3) * 2}
                              y1={innerRect1Y}
                              x2={innerRect1X + (innerRect1Width / 3) * 2}
                              y2={innerRect1Y + innerRect1Height}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                            {/* Horizontal line */}
                            <line
                              x1={innerRect1X}
                              y1={innerRect1Y + innerRect1Height / 2}
                              x2={innerRect1X + innerRect1Width}
                              y2={innerRect1Y + innerRect1Height / 2}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                          </g>
                        )}
                        {gridStructure === '2x2' && (
                          <g>
                            {/* Render lines for 2x2 grid */}
                            {/* Vertical line */}
                            <line
                              x1={innerRect1X + innerRect1Width / 2}
                              y1={innerRect1Y}
                              x2={innerRect1X + innerRect1Width / 2}
                              y2={innerRect1Y + innerRect1Height}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                            {/* Horizontal line */}
                            <line
                              x1={innerRect1X}
                              y1={innerRect1Y + innerRect1Height / 2}
                              x2={innerRect1X + innerRect1Width}
                              y2={innerRect1Y + innerRect1Height / 2}
                              stroke={getContrastingColor(keycapColor)}
                              strokeWidth="0.1"
                            />
                          </g>
                        )}
                      </g>
                    )}

                    {/* Render lines based on innerRect2 if isoSection is 'main' */}
                    {isoSection === 'main' &&
                      innerRect2X !== null &&
                      innerRect2Y !== null &&
                      innerRect2Width !== null &&
                      innerRect2Height !== null && (
                        <g>
                          {gridStructure === '3x3' && (
                            <g>
                              {/* Render lines for 3x3 grid */}
                              {/* Vertical lines */}
                              <line
                                x1={innerRect2X + innerRect2Width / 3}
                                y1={innerRect2Y}
                                x2={innerRect2X + innerRect2Width / 3}
                                y2={innerRect2Y + innerRect2Height}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                              <line
                                x1={innerRect2X + (innerRect2Width / 3) * 2}
                                y1={innerRect2Y}
                                x2={innerRect2X + (innerRect2Width / 3) * 2}
                                y2={innerRect2Y + innerRect2Height}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                              {/* Horizontal lines */}
                              <line
                                x1={innerRect2X}
                                y1={innerRect2Y + innerRect2Height / 3}
                                x2={innerRect2X + innerRect2Width}
                                y2={innerRect2Y + innerRect2Height / 3}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                              <line
                                x1={innerRect2X}
                                y1={innerRect2Y + (innerRect2Height / 3) * 2}
                                x2={innerRect2X + innerRect2Width}
                                y2={innerRect2Y + (innerRect2Height / 3) * 2}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                            </g>
                          )}
                          {gridStructure === '3x2' && (
                            <g>
                              {/* Render lines for 3x2 grid */}
                              {/* Vertical line */}
                              <line
                                x1={innerRect2X + innerRect2Width / 2}
                                y1={innerRect2Y}
                                x2={innerRect2X + innerRect2Width / 2}
                                y2={innerRect2Y + innerRect2Height}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                              {/* Horizontal lines */}
                              <line
                                x1={innerRect2X}
                                y1={innerRect2Y + innerRect2Height / 3}
                                x2={innerRect2X + innerRect2Width}
                                y2={innerRect2Y + innerRect2Height / 3}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                              <line
                                x1={innerRect2X}
                                y1={innerRect2Y + (innerRect2Height / 3) * 2}
                                x2={innerRect2X + innerRect2Width}
                                y2={innerRect2Y + (innerRect2Height / 3) * 2}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                            </g>
                          )}
                          {gridStructure === '2x3' && (
                            <g>
                              {/* Render lines for 2x3 grid */}
                              {/* Vertical lines */}
                              <line
                                x1={innerRect2X + innerRect2Width / 3}
                                y1={innerRect2Y}
                                x2={innerRect2X + innerRect2Width / 3}
                                y2={innerRect2Y + innerRect2Height}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                              <line
                                x1={innerRect2X + (innerRect2Width / 3) * 2}
                                y1={innerRect2Y}
                                x2={innerRect2X + (innerRect2Width / 3) * 2}
                                y2={innerRect2Y + innerRect2Height}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                              {/* Horizontal line */}
                              <line
                                x1={innerRect2X}
                                y1={innerRect2Y + innerRect2Height / 2}
                                x2={innerRect2X + innerRect2Width}
                                y2={innerRect2Y + innerRect2Height / 2}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                            </g>
                          )}
                          {gridStructure === '2x2' && (
                            <g>
                              {/* Render lines for 2x2 grid */}
                              {/* Vertical line */}
                              <line
                                x1={innerRect2X + innerRect2Width / 2}
                                y1={innerRect2Y}
                                x2={innerRect2X + innerRect2Width / 2}
                                y2={innerRect2Y + innerRect2Height}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                              {/* Horizontal line */}
                              <line
                                x1={innerRect2X}
                                y1={innerRect2Y + innerRect2Height / 2}
                                x2={innerRect2X + innerRect2Width}
                                y2={innerRect2Y + innerRect2Height / 2}
                                stroke={getContrastingColor(keycapColor)}
                                strokeWidth="0.1"
                              />
                            </g>
                          )}
                        </g>
                      )}
                  </g>
                );
              }
              return null;
            })}

        {/* applying the unique clipPath to ensure text is clipped within the innerDPath1mmPadding */}
        <g clipPath={`url(#${clipPathId})`}>
          {bbox &&
            texts &&
            Object.keys(texts).length > 0 && // Ensure texts is defined and not empty
            Object.entries(texts)
              .filter(
                ([_, { text, visible }]) =>
                  text && text.trim() !== '' && visible
              ) // filtering out empty strings (this ensures that only text elements are being rendered onto the keycaps, if the string is not empty)
              .map(([index, entry]) => {
                const { text, customStyles, style, isActiveStates } = entry;
                const font = fonts[fontCacheKey(style.fontFamily)]; // get the font

                if (!font) return null; // return, if the font is not loaded yet

                const {
                  box,
                  transform,
                  text: textPosition,
                  highlight,
                  underline,
                  strikethrough,
                } = textLegendLayout(entry, font, printableAreas); // the legend's geometry over its fontkit font (utils/textLegend.js): the printing files read the text's x, y and style back

                return (
                  <g key={index} transform={transform}>
                    {highlight && (
                      <rect
                        {...highlight}
                        fill={customStyles.highlight.color}
                      />
                    )}

                    {selectedEditor === 'text' &&
                    showBoundingBox &&
                    selectedKeycapRowIndex === rowIndex &&
                    selectedKeycapIndex === keycapIndex &&
                    Number(selectedTextIndex) === Number(index) ? (
                      <rect
                        className="dont-include-in-download" // don't include the visualization of the bbox in the svg and png downloads
                        {...box}
                        fill="green"
                      />
                    ) : null}

                    {/* rendering the text: */}
                    <text
                      x={textPosition.x}
                      y={textPosition.y}
                      textAnchor="start"
                      style={textLegendStyle(
                        style,
                        isActiveStates,
                        dataComingFromLayoutEditor
                      )}
                      dominantBaseline="alphabetic"
                    >
                      {text}
                    </text>

                    {/* rendering the custom underline */}
                    {underline && (
                      <line
                        {...underline}
                        {...decorationLineStyle(customStyles.underline)}
                      />
                    )}

                    {/* rendering the custom strikethrough */}
                    {strikethrough && (
                      <line
                        {...strikethrough}
                        {...decorationLineStyle(customStyles.strikethrough)}
                      />
                    )}
                  </g>
                );
              })}
        </g>
      </g>
    );
  }
);

export default RenderKeycapTexts;
